import { Box, Button, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import HttpService from "../../hooks/Https-services";
import { APIRoutes } from "../../utils/constants";
import useConfig from "../../hooks/useConfig";

// HTPTV-636: the buyer's "gifts you've sent" panel (status + resend).
const STATUS: Record<string, { label: string; color: any }> = {
  purchased: { label: "Processing", color: "warning" },
  scheduled: { label: "Scheduled", color: "info" },
  delivered: { label: "Delivered", color: "info" },
  redeemed: { label: "Redeemed", color: "success" },
  converted: { label: "Subscribed", color: "success" },
  expired: { label: "Expired", color: "default" },
};

const tierLabel = (t: string) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");
const termText = (b: string, n: number) =>
  `${n} ${b === "annual" ? "year" : "month"}${n > 1 ? "s" : ""}`;

const SentGifts = () => {
  const configData = useConfig();
  const primary = configData?.primary;
  const [gifts, setGifts] = useState<any[]>([]);
  const [resending, setResending] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await HttpService.get(APIRoutes.GiftSubscriptionSent);
        if (Array.isArray(res?.data?.data)) setGifts(res.data.data);
      } catch (e) {
        // no sent gifts / not available
      }
    })();
  }, []);

  if (!gifts.length) return null;

  const handleResend = async (id: number) => {
    setResending(id);
    try {
      await HttpService.post(APIRoutes.ResendGiftSubscription.replace(":id", String(id)), {});
      toast.success("Gift email resent");
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Could not resend");
    } finally {
      setResending(null);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: "16px", border: "1px solid", borderColor: "divider", mt: 3, p: { xs: 2.5, md: 3 } }}
    >
      <Typography variant="body2" fontWeight={700} color="text.secondary" letterSpacing={1.2} mb={2} textTransform="none">
        Gifts you've sent
      </Typography>
      <Stack divider={<Divider />}>
        {gifts.map((g) => {
          const st = STATUS[g.state] || { label: g.state, color: "default" };
          return (
            <Stack
              key={g.id}
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
              spacing={1}
              py={1.5}
            >
              <Box>
                <Typography variant="body2" fontWeight={600} textTransform="none">
                  {g.recipientName || g.recipientEmail}
                </Typography>
                <Typography variant="caption" color="text.secondary" textTransform="none">
                  {tierLabel(g.planTier)} · {termText(g.billingModel, g.termCount)}
                  {g.recipientName ? ` · ${g.recipientEmail}` : ""}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Chip size="small" label={st.label} color={st.color} variant="outlined" />
                {g.state === "delivered" && (
                  <Button
                    size="small"
                    disabled={resending === g.id}
                    onClick={() => handleResend(g.id)}
                    sx={{ color: primary, textTransform: "none" }}
                  >
                    {resending === g.id ? "Resending…" : "Resend"}
                  </Button>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default SentGifts;
